import humps from 'humps';
import * as RouteParser from 'route-parser';

import hashHistory from '@src/History';
import { dispatch } from '@src/dispatcher';
// Constants
import { ActionTypes, GRAPH_NODE_TYPE } from '@src/constants/MogamboConstants';
import * as ROUTES from '@src/constants/Routes';
import { PERMISSION_LEVELS } from '@src/const';

// Api
import * as Api from '@src/api';

// Stores
import CommonStore from '@src/stores/CommonStore';

// Util Actions
import { userHasPermission, permissionErrorAlert } from '@src/actions/_utils';

/**
 * Fetches the metadata (current step/node, story, substory, bot)
 * depending on the url parameters, and the current route, different api
 * calls are made to fetch data at different depths
 * @param {[type]} params [description]
 */
export async function setURLMetadata(
  path = window.location.hash.slice(1),
  callback,
  force
) {
  const nodeRoute = new RouteParser(`${ROUTES.NODE}(*else)`);
  const nodeMatchParams = nodeRoute.match(path);

  const aissNodeRoute = new RouteParser(`${ROUTES.NEW_AISS_NODE}(*else)`);
  const aissNodeParams = aissNodeRoute.match(path);

  const codeNodeRoute = new RouteParser(`${ROUTES.NEW_CODE_NODE}(*else)`);
  const codeNodeParams = codeNodeRoute.match(path);

  const dynamicNodeRoute = new RouteParser(`${ROUTES.DYNAMIC_NODE}(/:else)`);
  const dynamicNodeParams = dynamicNodeRoute.match(path);

  const connectionRoute = new RouteParser(`${ROUTES.CONNECTION}(/:else)`);
  const connectionMatchParams = connectionRoute.match(path);

  const smallTalkRoute = new RouteParser(`${ROUTES.SMALLTALK}(/:else)`);
  const smallTalkMatchParams = smallTalkRoute.match(path);

  const subStoryRoute = new RouteParser(`${ROUTES.NODES}(/:else)`);
  const subStoryMatchParams = subStoryRoute.match(path);

  const storyRoute = new RouteParser(`${ROUTES.SUBSTORIES}(/:else)`);
  const storyMatchParams = storyRoute.match(path);

  const botRoute = new RouteParser('/bot/:botId(/:else)');
  const botMatchParams = botRoute.match(path);

  const webGuideRoute = new RouteParser(`/webguide/:botId/:businessIdentifier`);
  const webGuideParams = webGuideRoute.match(path);

  const domainGeneralNodesRoute = new RouteParser('/bot/:botId/general-nodes');
  const domainGeneralMatchParams = domainGeneralNodesRoute.match(path);

  const storyGeneralNodesRoute = new RouteParser(
    '/bot/:botId/:storyId/general-nodes'
  );
  const storyGeneralMatchParams = storyGeneralNodesRoute.match(path);
  const agentRoute = new RouteParser(`${ROUTES.AGENT_ESCALATION}(/:else)`);
  const agentMatchParams = agentRoute.match(path);

  let params = null;
  if (dynamicNodeParams && dynamicNodeParams.storyId) {
    params = dynamicNodeParams;
  } else if (codeNodeParams && codeNodeParams.storyId) {
    params = codeNodeParams;
  } else if (aissNodeParams && aissNodeParams.storyId) {
    params = aissNodeParams;
  } else if (nodeMatchParams && nodeMatchParams.nodeId) {
    params = nodeMatchParams;
  } else if (subStoryMatchParams && subStoryMatchParams.subStoryId) {
    params = subStoryMatchParams;
  } else if (smallTalkMatchParams && smallTalkMatchParams.botId) {
    params = smallTalkMatchParams;
  } else if (storyMatchParams && storyMatchParams.storyId) {
    params = storyMatchParams;
  } else if (botMatchParams && botMatchParams.botId) {
    params = botMatchParams;
  } else if (domainGeneralMatchParams && domainGeneralMatchParams.botId) {
    params = domainGeneralMatchParams;
  } else if (
    webGuideParams &&
    webGuideParams.businessIdentifier &&
    webGuideParams.botId
  ) {
    params = webGuideParams;
  } else if (storyGeneralMatchParams && storyGeneralMatchParams.storyId) {
    params = storyGeneralMatchParams;
  } else if (connectionMatchParams && connectionMatchParams.storyId) {
    params = connectionMatchParams;
  } else if (agentMatchParams && agentMatchParams.botId) {
    params = agentMatchParams;
  }

  const meta = CommonStore.getMetaTags();
  if (!params) {
    if (callback) callback(meta);
    return;
  }
  if (
    params.storyId === meta.story.id &&
    params.subStoryId === meta.subStory.id &&
    params.nodeId === meta.step.id &&
    params.botId === meta.domain.id &&
    !force
  ) {
    if (callback) callback(meta);
    return;
  }

  let metaInfo = {};

  if (params.from) {
    metaInfo = await Api.getMetaInfoAsync('step', params.from);
    metaInfo.response.meta.step = { id: '', title: '' };
    metaInfo.response.meta.connection = {
      source: params.from,
      target: params.to,
    };
  } else if (params.nodeId) {
    if (params.nodeId.startsWith('new')) {
      metaInfo = await Api.getMetaInfoAsync('subStory', params.subStoryId);
      metaInfo.response.meta.step.id = params.nodeId;
      metaInfo.response.meta.step.isNew = true;
    } else if (path.includes('dynamic')) {
      metaInfo = await Api.getMetaInfoAsync(
        GRAPH_NODE_TYPE.OUTPUT,
        params.nodeId
      );
    } else if (path.includes('code')) {
      metaInfo = await Api.getMetaInfoAsync(
        GRAPH_NODE_TYPE.CODE,
        params.nodeId
      );
    } else if (path.includes('aiss')) {
      metaInfo = await Api.getMetaInfoAsync('aiss_intent', params.nodeId);
    } else {
      metaInfo = await Api.getMetaInfoAsync('step', params.nodeId);
    }
  } else if (params.subStoryId) {
    metaInfo = await Api.getMetaInfoAsync('subStory', params.subStoryId);
  } else if (params.storyId) {
    metaInfo = await Api.getMetaInfoAsync('story', params.storyId);
  } else if (params.botId) {
    metaInfo = await Api.getMetaInfoAsync('domain', params.botId);
  }

  if (metaInfo.status) {
    dispatch(ActionTypes.SET_URL_METADATA, {
      metaTags: metaInfo.response.meta,
    });
  }
  if (callback) callback(meta);
}

/**
 * Fetch step connections from the API and dispatch an action to update the
 * store.
 * @param {[type]}   stepId [description]
 * @param {Function} cb     [description]
 */
export async function setStepConnections(stepId, cb) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }

  if (stepId.includes('new')) return;
  const response = await Api.getConnectionsToNodeAsync(stepId);
  if (response.success) {
    const { body } = response;
    dispatch(ActionTypes.SET_NODE_CONNECTIONS, {
      connections: body.from_nodes,
    });
    if (cb) cb(body.from_nodes);
  }
}

/**
 * Make an API call to update the step connections on the backend
 * @param  {[type]}   stepId      [description]
 * @param  {[type]}   connections [description]
 * @param  {Function} cb          [description]
 * @return {[type]}               [description]
 */
export async function updateStepConnections(stepId, connections, cb) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    permissionErrorAlert();
    if (cb) cb(false);
  }
  const data = {
    action_type: 'update',
    from_nodes: connections,
  };

  const response = await Api.setConnectionsToNodeAsync(stepId, data);
  dispatch(ActionTypes.UPDATE_NODE_CONNECTIONS, {
    connections,
  });

  if (cb) cb(response.success, response);
}

export async function deleteStepConnections(
  stepId,
  connectionsToDelete,
  connections,
  cb
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    permissionErrorAlert();
    if (cb) cb(false);
  }
  const data = {
    action_type: 'delete',
    from_nodes: connectionsToDelete,
  };
  const response = await Api.deleteConnectionsToNodeAsync(stepId, data);
  dispatch(ActionTypes.UPDATE_NODE_CONNECTIONS, {
    connections,
  });

  if (cb) cb(response.success);
}

/**
 * Fetches the SDK credentials for a particular bot. It fetches all the nec. data
 * to initialize and run the Haptik Javascript SDK
 * @param  {[type]} botId [description]
 * @return {[type]}       [description]
 */
export async function getSDKCredentials(botId) {
  return Api.fetchTestConfiguration({ bot_id: botId });
}
/**
 * Route generator. Generates actual routes from route definitions. So a route
 * like /stories/:storyId gets converted to /stories/afnw3425nkjsdnf where
 * :storyId gets replaced with the actual storyId (afnw3425nkjsdnf) passed
 * as the meta parameter.
 *
 * @param  {[type]} route [description]
 * @param  {[type]} meta  [description]
 * @return {[type]}       [description]
 */
export function generateRoute(route, meta) {
  const { domain, subStory, story, step, connection = {} } = meta;
  return route
    .replace(':botId', domain.id)
    .replace(':storyId', story.id)
    .replace(':subStoryId', subStory.id)
    .replace(':nodeId', step.id)
    .replace(':sourceType', connection.sourceType)
    .replace(':sourceId', connection.sourceId)
    .replace(':destType', connection.destType)
    .replace(':destId', connection.destId);
}

/**
 * Dispatch an action to enable the save button for node pages (user says, bot says, etc)
 * It passes the callback that must be called when the user actually hits the save
 * button
 * @param  {[type]} saveCallback [description]
 * @return {[type]}              [description]
 */
export function enableSave(saveCallback) {
  CommonStore.setEditMode(true);
  dispatch(ActionTypes.ENABLE_SAVE, { saveCallback });
}

/**
 * Disables the save button by dispatching an action to the store
 * It's disabled by the story by clearing the save callback value
 * @return {[type]} [description]
 */
export function disableSave() {
  CommonStore.setEditMode(false);
  dispatch(ActionTypes.DISABLE_SAVE, {});
}

/**
 * Navigate to a particular story route (changes the URL)
 * @param  {[type]} meta       [description]
 * @param  {[type]} storyId [description]
 * @return {[type]}            [description]
 */
export function navToStory(meta, storyId) {
  if (storyId) {
    meta.story.id = storyId;
  }
  hashHistory.push(generateRoute(ROUTES.SUBSTORIES, meta));
}

/**
 * Navigate to a particular sub story route (changes the URL)
 * @param  {[type]} meta       [description]
 * @param  {[type]} subStoryId [description]
 * @return {[type]}            [description]
 */
export function navToSubStory(meta, subStoryId) {
  if (subStoryId) {
    meta.subStory.id = subStoryId;
  }
  hashHistory.push(generateRoute(ROUTES.NODES, meta));
}

/**
 * Navigate to a particular node (step) route (changes the URL)
 * @param  {[type]} meta   [description]
 * @param  {[type]} stepId [description]
 * @return {[type]}        [description]
 */
export function navToStep(meta, stepId, subStoryId) {
  if (stepId) {
    meta.step.id = stepId;
  }
  if (subStoryId) {
    meta.subStory.id = subStoryId;
  }
  let path = 'bot-says';
  try {
    const splitList = window.location.hash.split('nodes');
    if (splitList.length > 1) {
      path = splitList[1].split('/')[2] || 'bot-says';
    }
  } catch (e) {
    console.log('error', e);
  }
  const URL = generateRoute(ROUTES.NODE, meta);
  hashHistory.push(`${URL}/${path}`);
}

/**
 * Navigate to a particular node submenu. (ex. user-says, bot-says, settings etc.)
 * @param  {[type]} meta [description]
 * @param  {[type]} path [description]
 * @return {[type]}      [description]
 */
export function navToNodeSubMenu(meta, path) {
  const URL = generateRoute(ROUTES.NODE, meta);
  hashHistory.push(`${URL}/${path}`);
}

export function navToConnectionSubMenu(meta, path) {
  const URL = generateRoute(ROUTES.CONNECTION, meta);
  hashHistory.push(`${URL}/${path}`);
}

export function navToAndFromConnectionSubMenu(meta, match, path) {
  const connection = {};
  connection.sourceId = match.params.sourceId;
  connection.sourceType = match.params.sourceType;
  connection.destId = match.params.destId;
  connection.destType = match.params.destType;

  const newMeta = { ...meta, connection };

  navToConnectionSubMenu(newMeta, path);
}

/**
 * Navigate to the new step route (changes the URL)
 * @param  {[type]} meta [description]
 * @return {[type]}      [description]
 */
export function navToNewStep(meta) {
  const URL = generateRoute(ROUTES.NEW_NODE, meta);
  hashHistory.push(`${URL}`);
}

export function navToAissStep(meta, path) {
  const URL = generateRoute(ROUTES.NEW_AISS_NODE, meta);
  hashHistory.push(`${URL}`);
}

export function navToAissCodeStep(meta, path) {
  const URL = generateRoute(ROUTES.NEW_AISS_NODE, meta);
  hashHistory.push(`${URL}/${path}`);
}

export function navToCodeStep(meta, path) {
  const URL = generateRoute(ROUTES.NEW_CODE_NODE, meta);
  hashHistory.push(`${URL}/${path}`);
}

export function navToDynamicStep(meta, path) {
  const URL = generateRoute(ROUTES.DYNAMIC_NODE, meta);
  hashHistory.push(`${URL}/${path}`);
}

export const navToInterchange = (meta, match, path) => {
  const connection = {};
  connection.sourceId = match.params.destId;
  connection.sourceType = match.params.destType;
  connection.destId = match.params.sourceId;
  connection.destType = match.params.sourceType;

  const newMeta = { ...meta, connection };

  navToConnectionSubMenu(newMeta, path);
};

export function resetData() {
  dispatch(ActionTypes.RESET_EDITED, {});
}

export function selectSubstory(id) {
  dispatch(ActionTypes.SET_CURRENT_SUBSTORY_ID, { id });
}

export function setDroppable(droppable) {
  dispatch(ActionTypes.SET_DROPPABLE, { droppable });
}

export async function createNode(nodeData) {
  const body = humps.decamelizeKeys(nodeData);
  const response = await Api.createNode(body);
  return response;
}
